import { useMemo, useState } from "react";

import { Link, Select } from "@stellar/design-system";

import { DialogModal } from "@/components/DialogModal";

import { USER_ROLES_ARRAY } from "@/constants/settings";

import { userRoleText } from "@/helpers/userRoleText";

import { AboutRolesModal } from "./AboutRolesModal";

import { ApiUser, UserRole } from "@/types";

import "./ChangeRoleModal.scss";

interface ChangeRoleModalProps {
  visible: boolean;
  user: ApiUser | null;
  isPending: boolean;
  /** Owner confirmed the pick. Receives the newly-selected role. */
  onConfirm: (nextRole: UserRole) => void;
  onClose: () => void;
}

const displayName = (user: ApiUser | null) => {
  if (!user) return "this user";
  const parts = [user.first_name, user.last_name].filter(Boolean);
  return parts.length ? parts.join(" ") : user.email;
};

/**
 * Change-role modal shown from the Team members kebab. Collapses the five
 * old "Change role to X" menu items into a single dropdown-driven picker.
 *
 * Interaction rules (mirror the standalone prototype):
 *  - Save button is disabled until the pick differs from the current role.
 *  - "About the roles" link opens a nested capability-matrix modal.
 *  - Cancel/close resets to the current role so re-opens don't leak state.
 *
 * The modal owns its own `selectedRole` state and is reset each time the
 * parent bumps the `user` prop (via `useMemo` recomputing `initialRole`).
 */
export const ChangeRoleModal = ({
  visible,
  user,
  isPending,
  onConfirm,
  onClose,
}: ChangeRoleModalProps) => {
  const initialRole = useMemo<UserRole | null>(() => user?.roles?.[0] ?? null, [user]);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(initialRole);
  const [isAboutOpen, setIsAboutOpen] = useState(false);

  // Reset the picker whenever we switch to a different user without unmount.
  if (visible && user && selectedRole === null && initialRole) {
    setSelectedRole(initialRole);
  }

  const handleClose = () => {
    setSelectedRole(initialRole);
    setIsAboutOpen(false);
    onClose();
  };

  const handleConfirm = () => {
    if (!selectedRole || selectedRole === initialRole) return;
    onConfirm(selectedRole);
  };

  const canSave = Boolean(selectedRole) && selectedRole !== initialRole && !isPending;

  return (
    <>
      <DialogModal
        visible={visible}
        onClose={handleClose}
        title={`Change role for ${displayName(user)}`}
        width="50"
        primaryAction={{
          label: "Cancel",
          variant: "tertiary",
          onClick: handleClose,
          disabled: isPending,
        }}
        secondaryAction={{
          label: "Save role",
          variant: "primary",
          onClick: handleConfirm,
          disabled: !canSave,
          isLoading: isPending,
        }}
      >
        <div className="ChangeRoleModal">
          <p className="ChangeRoleModal__helper">
            Their role sets what they can do in VIA Disbursement Platform. Only Owners and Financial
            controllers can be co-signers.
          </p>

          <Link
            role="button"
            size="sm"
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                name="info"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 8h.01"></path>
                <path d="M11 12h1v4h1"></path>
              </svg>
            }
            iconPosition="left"
            onClick={() => setIsAboutOpen(true)}
          >
            About the roles
          </Link>

          <Select
            fieldSize="md"
            id="change-role-select"
            label="Role"
            value={selectedRole ?? ""}
            disabled={isPending}
            onChange={(e) => setSelectedRole((e.target.value || null) as UserRole | null)}
          >
            {USER_ROLES_ARRAY.map((r) => (
              <option key={r} value={r}>
                {userRoleText(r)}
              </option>
            ))}
          </Select>
        </div>
      </DialogModal>

      <AboutRolesModal visible={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
    </>
  );
};
